import React, { useContext, useEffect, useState } from 'react';
import { FlowContext } from '../../core/flow-engine';
import { titleCaseForString } from '../../core/util';
import {
  RenderBox,
  RenderCashBox
} from '../FONASUMFTransferDetails/FONASUMFTransferDetails';
import styles from '../FONASUMFTransferDetails/FONASUMFTransferDetails.css';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import QuickSearch from '../QuickSearch/QuickSearch';
import ManualInstitutionReview from '../ManualInstitutionReview/ManualInstitutionReview';
import {
  questionsAnswered,
  answeredNo,
  shouldValidate
} from '../FONASUMFTransferDetails/utils';
import {
  FONASUCustodianBranchAddressInfo,
  FONASUCustodianSpecialAddressReview
} from '../FONASUCustodianBranchAddressInfo/FONASUCustodianBranchAddressInfo';

export const AccountInfoInput = ({
  fundsInBrokerage,
  preliquidated,
  custodianFunds,
  errorFlag,
  setErrorFlag,
  cusips,
  setCusips,
  specialBranchAddressRequired
}) => {
  return (
    <div>
      {specialBranchAddressRequired && (
        <div>
          <FONASUCustodianBranchAddressInfo />
        </div>
      )}

      {questionsAnswered(fundsInBrokerage) &&
        (answeredNo(fundsInBrokerage) ? (
          <RenderBox
            custodianFunds={custodianFunds}
            errorFlag={errorFlag}
            setErrorFlag={setErrorFlag}
            cusips={cusips}
            setCusips={setCusips}
          />
        ) : (
          <RenderCashBox preliquidated={preliquidated} />
        ))}
    </div>
  );
};

const FormTitle = ({ fundsInBrokerage, preliquidated, custodianName }) =>
  questionsAnswered(fundsInBrokerage, preliquidated) && (
    <>
      <div className="form-title">Enter your {custodianName} mutual funds:</div>
      <style jsx>{styles}</style>
    </>
  );

export const ProviderSelection = ({
  custodian,
  institutions,
  changeProvider
}) => {
  const { custodianID, custodianName } = custodian;

  return custodianID === '' ? (
    <>
      <QuickSearch
        institutions={institutions}
        setManualProviderName={details => changeProvider(details)}
        mode="FONASU"
      />
      <style jsx>{styles}</style>
    </>
  ) : (
    <>
      <div className="assets-title">Selected Provider: {custodianName}</div>
      <div className="funds-in-brokerage">
        <MFBeaconButton
          id="changeProviderBtn"
          data-name="changeProviderBtn"
          type="button"
          onClick={() => changeProvider({ custodianID: '', custodianName: '' })}
          variant="tertiary"
          label="Change"
        />
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export const getProvider = provider => {
  let branchAddressRequired = '';
  let custodianID = '';
  let custodianName = '';
  let toraParticipant = '';
  let customerServicePhone = '';
  let regularAddressLine1 = '';
  let regularAddressLine2 = '';
  let regularAddressCity = '';
  let regularAddressState = '';
  let regularAddressZip = '';
  let funds = [];

  if (typeof provider === 'string') {
    custodianName = provider;
  } else {
    branchAddressRequired = provider.branchAddressRequired;
    custodianID = provider.custodianID;
    custodianName = provider.custodianName;
    toraParticipant = provider.toraParticipant;
    customerServicePhone = provider.customerServicePhone;
    regularAddressLine1 = provider.regularAddressLine1;
    regularAddressLine2 = provider.regularAddressLine2;
    regularAddressCity = provider.regularAddressCity;
    regularAddressState = provider.regularAddressState;
    regularAddressZip = provider.regularAddressZip;
    funds = provider.funds || [];
  }

  return {
    branchAddressRequired,
    custodianID,
    custodianName: titleCaseForString(custodianName),
    toraParticipant,
    customerServicePhone,
    regularAddressLine1,
    regularAddressLine2,
    regularAddressCity,
    regularAddressState,
    regularAddressZip,
    funds
  };
};

const FONASUTransferReview = ({ specialBranchAddressRequired }) => {
  return (
    <div>
      {specialBranchAddressRequired && <FONASUCustodianSpecialAddressReview />}
      <ManualInstitutionReview readOnly />
    </div>
  );
};

const FONASUMFTransferFunding = ({
  editFunding,
  setEditFunding,
  showSuccess
}) => {
  const {
    appState: {
      store: {
        app: { custodian, externalAccounts },
        loadedData: {
          institutions,
          custodianFunds: institutionFunds = [],
          cusips: defaultCusips = [],
          fundingEditMode
        },
        preliquidated: preliquidatedDefault = null,
        fundsInBrokerage: fundsInBrokerageDefault = null
      }
    },
    formProps: { assetsInBrokerage, cashAssets },
    next
  } = useContext(FlowContext);

  /** RADIO BUTTON STATES START* */
  const [fundsInBrokerage, setFundsInBrokerage] = useState(
    fundsInBrokerageDefault
  );
  const [preliquidated, setPreliquidated] = useState(preliquidatedDefault);

  const fundsInBrokerageHandler = event => {
    const fundsInBrokerageFlag = event.target.value === 'fundsInBrokerageYes';
    setFundsInBrokerage(fundsInBrokerageFlag);
  };

  const preliquidatedHandler = event => {
    const preliquidatedFlag = event.target.value === 'preliquidatedYes';
    setPreliquidated(preliquidatedFlag);
  };
  /** RADIO BUTTON STATES END* */

  const [selectedCustodian, setSelectedCustodian] = useState(custodian);
  const [custodianFunds, setCustodianFunds] = useState(institutionFunds);
  const [errorFlag, setErrorFlag] = useState(false);
  const [cusips, setCusips] = useState(defaultCusips);
  const [
    specialBranchAddressRequired,
    setSpecialBranchAddressRequired
  ] = useState(false);

  const changeProvider = provider => {
    const selectedProvider = getProvider(provider);
    setSelectedCustodian(selectedProvider);
    setCustodianFunds(selectedProvider?.funds);
    setSpecialBranchAddressRequired(selectedProvider?.branchAddressRequired);
  };

  const isValid = shouldValidate(
    selectedCustodian,
    fundsInBrokerage,
    preliquidated,
    externalAccounts,
    editFunding,
    errorFlag
  );

  useEffect(() => {
    setEditFunding(fundingEditMode);
  }, [fundingEditMode]);

  const handleClick = async () => {
    if (editFunding) {
      setErrorFlag(false);
      await next({
        section: 'externalAccount',
        custodian: selectedCustodian,
        custodianFunds,
        cusips,
        fundsInBrokerage,
        preliquidated
      })();
    } else {
      await next('unlockRolloverFundingSection')();
    }
  };

  return (
    <div className="manual-institution">
      <div className="section-header">
        <SectionHeading text="My Transfer Details" />
      </div>

      {editFunding ? (
        <div className="container">
          <div className="assets-title-wrapper">
            <ProviderSelection
              custodian={selectedCustodian}
              institutions={institutions}
              changeProvider={changeProvider}
            />
          </div>

          {selectedCustodian.custodianName !== '' && (
            <>
              <div className="assets-title-wrapper">
                <div className="assets-title">
                  Are the assets currently in a brokerage account?
                </div>

                <div className="funds-in-brokerage">
                  <input
                    id="yes-brokerage"
                    name="fundsInBrokerage"
                    data-name="fundsInBrokerageYes"
                    onChange={fundsInBrokerageHandler}
                    type="radio"
                    value="fundsInBrokerageYes"
                    checked={fundsInBrokerage}
                    {...assetsInBrokerage}
                  />
                  <label htmlFor="yes-brokerage">Yes</label>

                  <input
                    id="no-brokerage"
                    name="fundsInBrokerage"
                    data-name="fundsInBrokerageNo"
                    onChange={fundsInBrokerageHandler}
                    type="radio"
                    value="fundsInBrokerageNo"
                    checked={answeredNo(fundsInBrokerage)}
                  />
                  <label htmlFor="no-brokerage">No</label>
                </div>
              </div>

              {fundsInBrokerage && (
                <>
                  <div className="assets-title-wrapper">
                    <div className="assets-title">
                      Have you converted the assets to cash prior to requesting
                      this transfer?
                    </div>
                    <div className="funds-in-brokerage">
                      <input
                        id="preliquidated"
                        name="preliquidated"
                        data-name="preliquidatedYes"
                        onChange={preliquidatedHandler}
                        type="radio"
                        value="preliquidatedYes"
                        checked={preliquidated}
                        {...cashAssets}
                      />
                      <label htmlFor="preliquidated">Yes</label>

                      <input
                        id="no-preliquidated"
                        name="preliquidated"
                        data-name="preliquidatedNo"
                        onChange={preliquidatedHandler}
                        type="radio"
                        value="preliquidatedNo"
                        checked={answeredNo(preliquidated)}
                      />
                      <label htmlFor="no-preliquidated">No</label>
                    </div>
                  </div>
                  <FormTitle
                    fundsInBrokerage={fundsInBrokerage}
                    preliquidated={preliquidated}
                    custodianName={selectedCustodian.custodianName}
                  />
                </>
              )}

              <AccountInfoInput
                fundsInBrokerage={fundsInBrokerage}
                preliquidated={preliquidated}
                custodianFunds={custodianFunds}
                errorFlag={errorFlag}
                setErrorFlag={setErrorFlag}
                cusips={cusips}
                setCusips={setCusips}
                specialBranchAddressRequired={specialBranchAddressRequired}
              />
            </>
          )}
        </div>
      ) : (
        <FONASUTransferReview
          specialBranchAddressRequired={specialBranchAddressRequired}
        />
      )}
      <EditButton
        onClick={handleClick}
        currentlyEditing={editFunding}
        disabled={!isValid || showSuccess}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default FONASUMFTransferFunding;
